var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useCallback, useState } from "react";
import DynamicTable from "@atlaskit/dynamic-table";
import Lozenge from "@atlaskit/lozenge";
import Toggle from "@atlaskit/toggle";
import { useMutationUI, usePollingQuery } from "../../hooks";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from "@atlaskit/modal-dialog";
import Button, { LoadingButton } from "@atlaskit/button";
import styled from "styled-components";
import { CronosConfigForServiceQuery, DisableCronosConfigMutation, EnableCronosConfigMutation } from "../../graphql";
import { ConfigDetailsEditModal } from "../FormView/CronosConfigEditForm";
import Tooltip from "@atlaskit/tooltip";
import { isConfigDisabled, isFailaConfig } from "../../utils/cronos";
import SectionMessage from "@atlaskit/section-message";
import { isEmpty } from "lodash";
var configMessages = {
    FailaConfig: "UI editing is disabled. Your service currently has a Faila resource configured via SD, please refer go/continuous-chaos-runbook.",
    default: "Click to enable/disable the configuration"
};
var configToolTipMessage = function (config) {
    return isFailaConfig(config) ? configMessages.FailaConfig : configMessages.default;
};
var head = {
    cells: [
        {
            key: "micros-env",
            content: "Micros Env",
            isSortable: true
        },
        {
            key: "fault-type",
            content: "Fault Type",
            isSortable: true
        },
        {
            key: "last-modified-by",
            content: "Last Modified By",
            isSortable: true
        },
        {
            key: "configuration",
            content: "Configuration",
            isSortable: true
        },
        {
            key: "status",
            content: "Status",
            isSortable: true
        }
    ]
};
export var CronosConfigsListView = function (props) {
    var _a;
    var _b = usePollingQuery(CronosConfigForServiceQuery, {
        variables: { serviceId: props.serviceId }
    }), loading = _b.loading, data = _b.data;
    var configs = (_a = data === null || data === void 0 ? void 0 : data.cronosConfigForService) !== null && _a !== void 0 ? _a : [];
    return (React.createElement("div", null,
        React.createElement("br", null),
        React.createElement(CronosConfigsDynamicTable, { configs: configs, serviceId: props.serviceId, isDataLoading: loading })));
};
var CronosConfigsDynamicTable = function (_a) {
    var configs = _a.configs, isDataLoading = _a.isDataLoading, serviceId = _a.serviceId;
    var _b = __read(useState(false), 2), isOpen = _b[0], setIsOpen = _b[1];
    var _c = __read(useState(false), 2), isStatusUpdateOpen = _c[0], setIsStatusUpdateOpen = _c[1];
    var openModal = useCallback(function () { return setIsOpen(true); }, []);
    var getUpdateStatus = function () {
        return configDetails.status === "enabled" ? "disable" : "enable";
    };
    var openStatusUpdateModal = useCallback(function () { return setIsStatusUpdateOpen(true); }, []);
    var closeStatusUpdateModal = useCallback(function () { return setIsStatusUpdateOpen(false); }, []);
    var _d = __read(useState({}), 2), configDetails = _d[0], setConfigDetails = _d[1];
    var _e = useMutationUI({
        mutation: configDetails.status === "enabled"
            ? DisableCronosConfigMutation
            : EnableCronosConfigMutation,
        successMessage: "config is queued to be " + getUpdateStatus() + ", This usually takes upto 30 seconds",
        options: {
            variables: {
                id: configDetails.id
            },
            onCompleted: function () {
                closeStatusUpdateModal();
            }
        }
    }), mutateEditStatus = _e.mutate, isLoading = _e.isLoading;
    return (React.createElement(Container, null,
        React.createElement(ModalTransition, null, isStatusUpdateOpen && (React.createElement(Modal, { onClose: closeStatusUpdateModal },
            React.createElement(ModalHeader, null,
                React.createElement(ModalTitle, null, "Update Config Status")),
            React.createElement(ModalBody, null,
                "Are you sure? This will ",
                React.createElement(Lozenge, null, getUpdateStatus()),
                " the config in ",
                configDetails.environment,
                " of fault type",
                " ",
                configDetails.faultType),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { appearance: "subtle", onClick: closeStatusUpdateModal }, "Cancel"),
                React.createElement(LoadingButton, { appearance: "primary", isLoading: isLoading, onClick: function () {
                        mutateEditStatus();
                    } }, "Continue"))))),
        React.createElement(ConfigDetailsEditModal, { isOpen: isOpen, configDetails: configDetails, setIsOpen: setIsOpen }),
        isEmpty(configs) && !isDataLoading && (React.createElement(SectionMessage, { appearance: "change" },
            "Your service isn't onboarded to continuous-chaos or migrated from Faila yet. Please refer this\u00A0",
            React.createElement("a", { href: "https://go.atlassian.com/faila-cc-migration", target: "_blank", rel: "noopener noreferrer" }, "page"),
            "\u00A0for more details.")),
        React.createElement(DynamicTable, { isLoading: isDataLoading, caption: "Continuous Chaos Configurations", head: head, isFixedSize: true, defaultSortKey: "micros-env", defaultSortOrder: "ASC", rows: (configs !== null && configs !== void 0 ? configs : []).map(function (config) { return ({
                key: "row-" + config.id,
                cells: [
                    {
                        key: "environment-" + config.environment,
                        content: config.environment
                    },
                    {
                        key: "fault-type-" + config.faultType,
                        content: config.faultType
                    },
                    {
                        key: "last-modified-by-" + config.lastModifiedBy,
                        content: config.lastModifiedBy
                    },
                    {
                        key: "config-parameters-" + config.id,
                        content: (React.createElement(Button, { appearance: "link", onClick: function (e) {
                                setConfigDetails(config);
                                openModal();
                            } }, "Details"))
                    },
                    {
                        key: "config-status-" + config.status,
                        content: (React.createElement(Tooltip, { content: configToolTipMessage(config) },
                            React.createElement(Container, null,
                                React.createElement(Toggle, { id: "toggle-" + config.id, isChecked: config.status === "enabled", isDisabled: isConfigDisabled(config), onChange: function (e) {
                                        setConfigDetails(config);
                                        openStatusUpdateModal();
                                    } }))))
                    }
                ]
            }); }), emptyView: React.createElement("div", null, "No Configurations Found"), rowsPerPage: 10 })));
};
export var Container = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  margin-top: 10px;\n"], ["\n  margin-top: 10px;\n"])));
var templateObject_1;
